import { useCallback, useEffect, useState, type CSSProperties, type FormEvent } from "react";
import type {
  AdminMemberDto,
  StudentBillingAdminBillingUnitDetailDto,
  StudentBillingAdminOverviewDto,
  StudentBillingAdminStudentDetailDto,
  StudentBillingPaymentAccountDto,
  StudentBillingPaymentExecutionDto
} from "@madamuh/domain";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { fetchAdminMembers } from "../../shared/api/adminDirectory";
import { ApiError } from "../../shared/api/client";
import {
  adjustStudentBillingAdminUnit,
  assignStudentBillingAdminPayer,
  createOneOffStudentBillingAdminUnit,
  createRegularStudentBillingAdminUnit,
  createStudentBillingAdminPayer,
  fetchStudentBillingAdminOverview,
  fetchStudentBillingAdminStudent,
  fetchStudentBillingAdminUnit,
  fetchStudentBillingAdminPaymentExecution,
  executeStudentBillingAdminPayment,
  retryStudentBillingAdminPayment,
  lockStudentBillingAdminUnit,
  putRegularStudentBillingAdminSetting,
  putStudentBillingAdminCollectionPreference
} from "../../shared/api/studentBillingAdmin";
import {
  createStudentBillingConnectOnboardingSession,
  createStudentBillingPaymentAccount,
  fetchStudentBillingPaymentAccount,
  refreshStudentBillingPaymentAccount
} from "../../shared/api/studentBillingPaymentAccount";
import {
  fetchReviewChargeProbeStatus,
  runReviewChargeGateDisabledProbe,
  runReviewPaidReplayProbe,
  type ReviewChargeProbeResult,
  type ReviewChargeProbeStatus
} from "../../shared/api/reviewStudentBillingChargeProbe";
import {
  getProtectedApiErrorMessage,
  isUnauthenticatedApiError
} from "../../shared/session/protectedApiError";
import { useProtectedRouteSession } from "../../shared/session/useProtectedRouteSession";
import { OperatorPageScaffold } from "../../shared/ui/protected/OperatorPageScaffold";

const pageLinks = [
  { to: "/t/:tenantSlug/teacher/admin", label: "管理ホーム" },
  { to: "/t/:tenantSlug/teacher/admin/contract", label: "契約・解約" },
  { to: "/t/:tenantSlug/teacher/admin/integrations", label: "連携設定" }
];

const cardStyle = {
  display: "grid",
  gap: 12,
  padding: 16,
  border: "1px solid #dbe4ea",
  borderRadius: 14,
  background: "#fff"
} satisfies CSSProperties;
const gridStyle = { display: "grid", gap: 12 } satisfies CSSProperties;
const inputStyle = { width: "100%", boxSizing: "border-box", padding: "10px 12px", border: "1px solid #b7c4cc", borderRadius: 8 } satisfies CSSProperties;
const buttonStyle = { padding: "10px 14px", border: 0, borderRadius: 9, background: "#176b55", color: "#fff", fontWeight: 700, cursor: "pointer" } satisfies CSSProperties;
const secondaryButtonStyle = { ...buttonStyle, background: "#e4eeeb", color: "#163e34" } satisfies CSSProperties;
const mutedStyle = { margin: 0, color: "#667085", fontSize: 13 } satisfies CSSProperties;

function today(): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Tokyo", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
}

function serviceMonth(): string {
  return today().slice(0, 7);
}

function nextServiceMonthStart(): string {
  const [year, month] = today().split("-").map(Number) as [number, number, number];
  const nextYear = month === 12 ? year + 1 : year;
  const nextMonth = month === 12 ? 1 : month + 1;
  return `${String(nextYear).padStart(4, "0")}-${String(nextMonth).padStart(2, "0")}-01`;
}

function opaqueId(prefix: string): string {
  return `${prefix}_${crypto.randomUUID().replaceAll("-", "")}`;
}

function yen(value: number | null | undefined): string {
  return value === null || value === undefined ? "未設定" : `${value.toLocaleString("ja-JP")}円`;
}

function dateTimeJa(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "日時不明"
    : new Intl.DateTimeFormat("ja-JP", { timeZone: "Asia/Tokyo", dateStyle: "medium", timeStyle: "short" }).format(date);
}

function payerLabel(payer: StudentBillingAdminStudentDetailDto["payers"][number], members: readonly AdminMemberDto[]): string {
  const member = payer.platformUserId ? members.find((item) => item.platformUserId === payer.platformUserId) : null;
  return member?.appNickname ?? member?.lineDisplayNameSnapshot ?? "登録済み支払者";
}

export function studentBillingAdjustmentActorLabel(
  actorPlatformUserId: string,
  members: readonly AdminMemberDto[]
): string {
  const member = members.find((item) => item.platformUserId === actorPlatformUserId);
  const appNickname = member?.appNickname?.trim();
  if (appNickname) return appNickname;
  const lineDisplayNameSnapshot = member?.lineDisplayNameSnapshot?.trim();
  return lineDisplayNameSnapshot || actorPlatformUserId;
}

function paymentLabel(payer: StudentBillingAdminStudentDetailDto["payers"][number] | null): string {
  if (!payer) return "支払者未設定";
  if (!payer.paymentMethod.paymentMethodReady || !payer.paymentMethod.card) return "カード未登録";
  const card = payer.paymentMethod.card;
  return `${card.brand} •••• ${card.last4}（${card.expiryMonth}/${card.expiryYear}）`;
}

function paymentAccountLabel(status: StudentBillingPaymentAccountDto["status"]): string {
  return status === "notStarted" ? "Stripe連携未開始"
    : status === "onboardingPending" ? "Stripe設定手続き中"
      : status === "restricted" ? "Stripe確認事項あり"
        : status === "ready" ? "Stripe決済利用可能"
          : "Stripe連携停止中";
}

function contractModeLabel(mode: "paymentProcessing" | "management"): string {
  return mode === "paymentProcessing" ? "決済利用型" : "管理利用型";
}

function collectionMethodLabel(method: "card" | "tenantCollection"): string {
  return method === "card" ? "カード決済" : "テナント徴収";
}

function collectionSourceLabel(source: "paymentProcessingDefault" | "explicitPreference" | "managementOverride"): string {
  return source === "paymentProcessingDefault" ? "契約既定" : source === "explicitPreference" ? "個別設定" : "管理利用型固定";
}

function contractStatusLabel(status: "trialing" | "active" | "cancellationScheduled" | "ended"): string {
  return status === "trialing" ? "無料利用期間中"
    : status === "active" ? "契約中"
      : status === "cancellationScheduled" ? "解約予定"
        : "契約終了";
}

function restrictionStatusLabel(status: "none" | "paymentRestricted" | "riskSuspended" | "operatorSuspended"): string {
  return status === "none" ? "制限なし"
    : status === "paymentRestricted" ? "支払い制限中"
      : status === "riskSuspended" ? "審査により一時停止中"
        : "運営により一時停止中";
}

function ReviewChargeProbePanel({ tenantSlug, requested }: { tenantSlug: string; requested: boolean }) {
  const [status, setStatus] = useState<ReviewChargeProbeStatus | null>(null);
  const [billingUnitId, setBillingUnitId] = useState("");
  const [expectedExecutionId, setExpectedExecutionId] = useState("");
  const [result, setResult] = useState<ReviewChargeProbeResult | null>(null);
  const [probeState, setProbeState] = useState<"idle" | "running">("idle");
  const [safeError, setSafeError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setStatus(null);
    setResult(null);
    setSafeError(null);
    if (!requested) return () => { active = false; };
    void fetchReviewChargeProbeStatus(tenantSlug)
      .then((value) => { if (active) setStatus(value); })
      .catch(() => { if (active) setStatus(null); });
    return () => { active = false; };
  }, [requested, tenantSlug]);

  if (!requested || !status) return null;

  async function run(action: () => Promise<ReviewChargeProbeResult>) {
    setProbeState("running");
    setResult(null);
    setSafeError(null);
    try {
      setResult(await action());
    } catch (error) {
      setSafeError(error instanceof ApiError && error.code ? error.code : "REVIEW_CHARGE_PROBE_FAILED");
    } finally {
      setProbeState("idle");
    }
  }

  return (
    <section style={{ ...cardStyle, borderColor: "#8f6b00", background: "#fffaf0" }} data-review-charge-probe="true">
      <h2 style={{ margin: 0 }}>Review限定・決済安全性検証</h2>
      <p style={mutedStyle}>通常の請求操作ではありません。サーバーがReview debug、同一tenantのactive admin、exact ownerを確認した場合だけ表示されます。</p>
      <p style={mutedStyle}>
        Read: {status.gates.readEnabled ? "ON" : "OFF"} / Write: {status.gates.writesEnabled ? "ON" : "OFF"} / Charge: {status.gates.chargeExecutionEnabled ? "ON" : "OFF"}
      </p>
      <input
        aria-label="Review検証用 billingUnitId"
        value={billingUnitId}
        onChange={(event) => setBillingUnitId(event.target.value)}
        style={inputStyle}
        autoComplete="off"
      />
      <button
        type="button"
        style={secondaryButtonStyle}
        disabled={probeState === "running" || billingUnitId.trim().length === 0}
        onClick={() => void run(() => runReviewChargeGateDisabledProbe(tenantSlug, billingUnitId.trim()))}
      >実行gate無効を検証</button>
      <input
        aria-label="Review検証用 expectedExecutionId"
        value={expectedExecutionId}
        onChange={(event) => setExpectedExecutionId(event.target.value)}
        style={inputStyle}
        autoComplete="off"
      />
      <button
        type="button"
        style={secondaryButtonStyle}
        disabled={probeState === "running" || billingUnitId.trim().length === 0 || expectedExecutionId.trim().length === 0}
        onClick={() => void run(() => runReviewPaidReplayProbe(tenantSlug, billingUnitId.trim(), expectedExecutionId.trim()))}
      >paid再実行の冪等性を検証</button>
      {result ? (
        <div role="status" style={{ padding: 12, borderRadius: 8, background: "#e9f7ef" }}>
          <strong>PASS: {result.probe}</strong>
          <p style={mutedStyle}>請求単位状態: {result.evidence.billingUnitStatus ?? "なし"}</p>
          <p style={mutedStyle}>実行数: {result.evidence.executionCount} / 試行数: {result.evidence.attemptCount} / 決済監査数: {result.evidence.paymentAuditCount}</p>
        </div>
      ) : null}
      {safeError ? <p role="alert">FAIL: {safeError}</p> : null}
    </section>
  );
}

export function AdminBillingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { tenantSlug = "unknown-tenant" } = useParams();
  const { routeStatus, sessionStatus, session, errorMessage, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const hasAdminRole = Boolean(session?.member?.roles.includes("admin"));
  const reviewChargeProbeRequested = searchParams.get("reviewChargeProbe") === "1";
  const [overview, setOverview] = useState<StudentBillingAdminOverviewDto | null>(null);
  const [paymentAccount, setPaymentAccount] = useState<StudentBillingPaymentAccountDto | null>(null);
  const [detail, setDetail] = useState<StudentBillingAdminStudentDetailDto | null>(null);
  const [unitDetail, setUnitDetail] = useState<StudentBillingAdminBillingUnitDetailDto | null>(null);
  const [paymentExecution, setPaymentExecution] = useState<StudentBillingPaymentExecutionDto | null>(null);
  const [members, setMembers] = useState<AdminMemberDto[]>([]);
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [mutationState, setMutationState] = useState<"idle" | "submitting">("idle");
  const [message, setMessage] = useState<string | null>(null);
  const [payerMemberId, setPayerMemberId] = useState("");
  const [assignmentPayerId, setAssignmentPayerId] = useState("");
  const [assignmentFrom, setAssignmentFrom] = useState(today());
  const [regularAmount, setRegularAmount] = useState("");
  const [regularFrom, setRegularFrom] = useState(today());
  const [regularEnrollmentId, setRegularEnrollmentId] = useState("");
  const [regularServiceMonth, setRegularServiceMonth] = useState(serviceMonth());
  const [oneOffAmount, setOneOffAmount] = useState("");
  const [oneOffSourceType, setOneOffSourceType] = useState("manual");
  const [oneOffSourceId, setOneOffSourceId] = useState("");
  const [adjustmentAmount, setAdjustmentAmount] = useState("");
  const [adjustmentReason, setAdjustmentReason] = useState("");
  const [collectionMethod, setCollectionMethod] = useState<"card" | "tenantCollection">("card");
  const [collectionFrom, setCollectionFrom] = useState(nextServiceMonthStart());
  const [collectionTo, setCollectionTo] = useState("");

  const handleError = useCallback((error: unknown, fallback: string) => {
    if (isUnauthenticatedApiError(error)) {
      navigate(`/t/${tenantSlug}/entry`, { replace: true });
      return;
    }
    if (error instanceof ApiError && error.code === "STUDENT_BILLING_VERSION_MISMATCH") {
      setMessage("他の操作で請求情報が更新されました。画面を再読み込みしてから内容を確認してください。自動再試行は行っていません。");
      return;
    }
    if (error instanceof ApiError && error.code === "STUDENT_BILLING_READ_DISABLED") {
      setMessage("生徒請求機能は現在無効です。設定が有効になるまで請求情報は表示・変更されません。");
      return;
    }
    setMessage(getProtectedApiErrorMessage(error, fallback));
  }, [navigate, tenantSlug]);

  const loadOverview = useCallback(async () => {
    setLoadState("loading");
    try {
      const [next, nextPaymentAccount] = await Promise.all([
        fetchStudentBillingAdminOverview(tenantSlug),
        fetchStudentBillingPaymentAccount(tenantSlug)
      ]);
      setOverview(next);
      setPaymentAccount(nextPaymentAccount);
      setSelectedStudentId((current) => current || next.students[0]?.studentId || "");
      const response = await fetchAdminMembers(tenantSlug);
      setMembers(response.items);
      setLoadState("idle");
    } catch (error) {
      setLoadState("error");
      handleError(error, "請求情報の取得に失敗しました");
    }
  }, [handleError, tenantSlug]);

  const loadStudent = useCallback(async (studentId: string) => {
    if (!studentId) {
      setDetail(null);
      return;
    }
    try {
      const next = await fetchStudentBillingAdminStudent(tenantSlug, studentId);
      setDetail(next);
      setRegularAmount(String(next.regularSetting?.amount ?? ""));
      setRegularFrom(next.regularSetting?.effectiveFrom ?? today());
      const activeAssignment = next.payerAssignments.find((assignment) => assignment.status === "active");
      setAssignmentPayerId(activeAssignment?.payerId ?? next.payers.find((payer) => payer.status === "active")?.payerId ?? "");
      setRegularEnrollmentId(next.paidRegularEnrollments[0]?.enrollmentId ?? "");
      setCollectionMethod(next.collectionPreference?.collectionMethod ?? (next.commercialAuthority.contractPricingMode === "management" ? "tenantCollection" : "card"));
      setCollectionFrom(next.collectionPreference?.effectiveFrom ?? nextServiceMonthStart());
      setCollectionTo(next.collectionPreference?.effectiveTo ?? "");
      setUnitDetail(null);
      setPaymentExecution(null);
    } catch (error) {
      handleError(error, "生徒の請求詳細を取得できませんでした");
    }
  }, [handleError, tenantSlug]);

  useEffect(() => {
    if (!isSessionReady || !hasAdminRole) return;
    void loadOverview();
  }, [hasAdminRole, isSessionReady, loadOverview]);

  useEffect(() => {
    if (overview && selectedStudentId) void loadStudent(selectedStudentId);
  }, [loadStudent, overview, selectedStudentId]);

  const refresh = useCallback(async () => {
    await loadOverview();
    if (selectedStudentId) await loadStudent(selectedStudentId);
  }, [loadOverview, loadStudent, selectedStudentId]);

  const mutate = useCallback(async (action: () => Promise<void>, success: string) => {
    setMutationState("submitting");
    setMessage(null);
    try {
      await action();
      await refresh();
      setMessage(success);
    } catch (error) {
      handleError(error, "請求情報を更新できませんでした");
    } finally {
      setMutationState("idle");
    }
  }, [handleError, refresh]);

  const currentDate = today();
  const activeAssignment = detail?.payerAssignments.find((assignment) =>
    assignment.status === "active"
    && assignment.effectiveFrom <= currentDate
    && (assignment.effectiveTo === null || assignment.effectiveTo >= currentDate)
  ) ?? null;
  const currentPayer = activeAssignment ? detail?.payers.find((payer) => payer.payerId === activeAssignment.payerId) ?? null : null;
  const unitPayer = unitDetail && detail
    ? detail.payers.find((payer) => payer.payerId === unitDetail.billingUnit.payerId) ?? null
    : null;
  const canAssignPayer = Boolean(detail?.capabilities.canAssignPayer);
  const canConfigureRegularAmount = Boolean(detail?.capabilities.canConfigureRegularAmount);
  const canCreateOneOff = Boolean(detail?.capabilities.canCreateOneOff);

  async function selectUnit(billingUnitId: string) {
    try {
      const [unit, execution] = await Promise.all([
        fetchStudentBillingAdminUnit(tenantSlug, billingUnitId),
        fetchStudentBillingAdminPaymentExecution(tenantSlug, billingUnitId)
      ]);
      setUnitDetail(unit);
      setPaymentExecution(execution);
      setAdjustmentAmount("");
      setAdjustmentReason("");
    } catch (error) {
      handleError(error, "請求単位の詳細を取得できませんでした");
    }
  }

  function submitPayer(event: FormEvent) {
    event.preventDefault();
    if (!payerMemberId) return;
    void mutate(() => createStudentBillingAdminPayer(tenantSlug, {
      payerId: opaqueId("payer"),
      payerType: "platformUser",
      platformUserId: payerMemberId,
      externalContactId: null,
      billingAuthorityAcceptedAt: null
    }), "支払者を登録しました。");
  }

  function submitAssignment(event: FormEvent) {
    event.preventDefault();
    if (!detail || !assignmentPayerId) return;
    void mutate(() => assignStudentBillingAdminPayer(tenantSlug, detail.student.studentId, {
      assignmentId: opaqueId("billing_assignment"),
      payerId: assignmentPayerId,
      effectiveFrom: assignmentFrom,
      effectiveTo: null
    }), "支払者を割り当てました。");
  }

  function submitRegularSetting(event: FormEvent) {
    event.preventDefault();
    if (!detail) return;
    void mutate(() => putRegularStudentBillingAdminSetting(tenantSlug, detail.student.studentId, {
      effectiveFrom: regularFrom,
      effectiveTo: null,
      amount: Number(regularAmount),
      currency: "JPY",
      expectedVersion: detail.regularSetting?.version ?? null
    }), "通常月謝を保存しました。");
  }

  function submitCollectionPreference(event: FormEvent) {
    event.preventDefault();
    if (!detail || !activeAssignment) return;
    void mutate(() => putStudentBillingAdminCollectionPreference(tenantSlug, detail.student.studentId, {
      payerAssignmentId: activeAssignment.assignmentId,
      collectionMethod,
      effectiveFrom: collectionFrom,
      effectiveTo: collectionTo || null,
      expectedVersion: detail.collectionPreference?.effectiveFrom === collectionFrom ? detail.collectionPreference.version : null
    }), "請求方法を保存しました。");
  }

  function submitRegularUnit(event: FormEvent) {
    event.preventDefault();
    if (!detail || !activeAssignment || !detail.regularSetting || !regularEnrollmentId) return;
    const setting = detail.regularSetting;
    void mutate(() => createRegularStudentBillingAdminUnit(tenantSlug, {
      studentId: detail.student.studentId,
      payerId: activeAssignment.payerId,
      payerAssignmentId: activeAssignment.assignmentId,
      paidEnrollmentId: regularEnrollmentId,
      serviceMonth: regularServiceMonth,
      defaultAmount: setting.amount,
      currency: "JPY",
      collectionRoute: "stripe"
    }), "通常月謝の請求単位を作成しました。");
  }

  function submitOneOff(event: FormEvent) {
    event.preventDefault();
    if (!detail || !activeAssignment) return;
    const sourceId = oneOffSourceId.trim() || opaqueId("manual_charge");
    void mutate(() => createOneOffStudentBillingAdminUnit(tenantSlug, {
      studentId: detail.student.studentId,
      payerId: activeAssignment.payerId,
      payerAssignmentId: activeAssignment.assignmentId,
      sourceType: oneOffSourceType,
      sourceId,
      defaultAmount: Number(oneOffAmount),
      currency: "JPY",
      collectionRoute: "stripe"
    }), "単発請求を作成しました。");
  }

  return (
    <OperatorPageScaffold
      currentArea="admin"
      tenantSlug={tenantSlug}
      tenantName={session?.tenant.displayName}
      session={session}
      title="生徒請求"
      description="支払担当・請求方法と、レッスン・イベント・単発調整の請求設定を管理します。"
      links={pageLinks}
      layoutVariant="wide"
    >
      <div style={gridStyle}>
        {routeStatus === "checking" || sessionStatus === "loading" ? <p>セッションを確認しています。</p> : null}
        {sessionStatus === "error" ? <p>セッション確認に失敗しました。{errorMessage}</p> : null}
        {routeStatus === "authenticated" && !hasAdminRole ? <p>この画面は有効な admin role のみ利用できます。</p> : null}
        {loadState === "loading" ? <p>請求情報を読み込んでいます。</p> : null}
        {message ? <p role="status" style={{ ...cardStyle, background: "#f5f9f8" }}>{message}</p> : null}
        <ReviewChargeProbePanel
          tenantSlug={tenantSlug}
          requested={reviewChargeProbeRequested && isSessionReady && hasAdminRole}
        />

        {isSessionReady && hasAdminRole ? (
          <section style={cardStyle}>
            <h2 style={{ margin: 0 }}>請求設定メニュー</h2>
            <p style={mutedStyle}>今後の月次請求に使う基準を、対象ごとに設定します。</p>
            <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))" }}>
              {([
                ["支払担当・請求方法", "支払者の登録、割り当て、カード・テナント徴収の設定", `/t/${tenantSlug}/teacher/admin/billing`],
                ["レッスン料金設定", "レギュラー月額と単発・短期コース総額", `/t/${tenantSlug}/teacher/admin/billing/lesson-fees`],
                ["イベント料金設定", "イベントごとの複数料金項目と対象月", `/t/${tenantSlug}/teacher/admin/billing/event-fees`],
                ["単発請求・調整", "生徒・レッスン単位の加算または減額", `/t/${tenantSlug}/teacher/admin/billing/manual-items`]
              ] as const).map(([title, description, to]) => (
                <Link key={to} to={to} style={{ ...cardStyle, textDecoration: "none", color: "inherit" }}>
                  <strong>{title}</strong>
                  <span style={mutedStyle}>{description}</span>
                </Link>
              ))}
              <Link to={`/t/${tenantSlug}/teacher/admin/billing/months/${serviceMonth()}`} style={{ ...cardStyle, textDecoration: "none", color: "inherit" }}>
                <strong>月次請求一覧</strong>
                <span style={mutedStyle}>生徒ごとのレッスン・イベント・単発調整を集計</span>
              </Link>
            </div>
          </section>
        ) : null}

        {overview ? (
          <>
            {paymentAccount ? (
              <section style={cardStyle}>
                <h2 style={{ margin: 0 }}>Stripe決済連携</h2>
                <strong>{paymentAccountLabel(paymentAccount.status)}</strong>
                <p style={mutedStyle}>決済受付: {paymentAccount.chargesAvailable ? "利用可能" : "利用不可"}</p>
                <p style={mutedStyle}>入金: {paymentAccount.payoutsAvailable ? "利用可能" : "利用不可"}</p>
                <p style={mutedStyle}>最終確認: {paymentAccount.lastCheckedAt ? dateTimeJa(paymentAccount.lastCheckedAt) : "未確認"}</p>
                {paymentAccount.attentionRequired ? <p style={mutedStyle}>Stripeで追加確認が必要です。</p> : null}
                {paymentAccount.capabilities.canManagePaymentAccount ? (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {paymentAccount.status === "notStarted" ? (
                      <button type="button" style={buttonStyle} disabled={mutationState === "submitting"} onClick={() => void mutate(async () => {
                        setPaymentAccount(await createStudentBillingPaymentAccount(tenantSlug));
                      }, "Stripe連携を開始しました。続けてStripe設定を完了してください。")}>Stripe連携を開始</button>
                    ) : null}
                    {paymentAccount.status !== "notStarted" && paymentAccount.status !== "ready" ? (
                      <button type="button" style={buttonStyle} disabled={mutationState === "submitting"} onClick={() => void mutate(async () => {
                        const session = await createStudentBillingConnectOnboardingSession(tenantSlug);
                        window.location.assign(session.onboardingUrl);
                      }, "Stripe設定画面を開きます。")}>Stripe設定を続ける</button>
                    ) : null}
                    {paymentAccount.status !== "notStarted" ? (
                      <button type="button" style={secondaryButtonStyle} disabled={mutationState === "submitting"} onClick={() => void mutate(async () => {
                        setPaymentAccount(await refreshStudentBillingPaymentAccount(tenantSlug));
                      }, "Stripe連携状態を更新しました。")}>状態を更新</button>
                    ) : null}
                  </div>
                ) : <p style={mutedStyle}>Stripe連携の変更はテナントオーナーのみ行えます。</p>}
              </section>
            ) : null}

            <section style={cardStyle}>
              <h2 style={{ margin: 0 }}>契約・請求方式</h2>
              <p style={{ margin: 0 }}>契約モード: <strong>{contractModeLabel(overview.commercialAuthority.contractPricingMode)}</strong></p>
              <p style={mutedStyle}>契約状態: {contractStatusLabel(overview.commercialAuthority.contractStatus)} / 制限状態: {restrictionStatusLabel(overview.commercialAuthority.serviceRestrictionStatus)}</p>
              <p style={mutedStyle}>請求方法は契約モードと生徒ごとの有効期間設定からサーバーが決定します。</p>
            </section>

            {!overview.capabilities.canAssignPayer ? (
              <section style={{ ...cardStyle, background: "#f8fafc" }}>
                <strong>閲覧専用</strong>
                <p style={mutedStyle}>このアカウントは請求情報を確認できますが、変更操作は行えません。</p>
              </section>
            ) : null}

            <section style={cardStyle}>
              <h2 style={{ margin: 0 }}>生徒一覧</h2>
              {overview.students.length === 0 ? <p style={mutedStyle}>表示できる生徒がいません。</p> : (
                <div style={{ display: "grid", gap: 8, gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))" }}>
                  {overview.students.map((student) => (
                    <button key={student.studentId} type="button" onClick={() => setSelectedStudentId(student.studentId)} style={{ ...secondaryButtonStyle, textAlign: "left", outline: selectedStudentId === student.studentId ? "2px solid #176b55" : "none" }}>
                      <span style={{ display: "block" }}>{student.displayName}</span>
                      <span style={{ display: "block", fontSize: 12, fontWeight: 400 }}>月謝 {yen(student.regularSetting?.amount)} / {paymentLabel(student.currentPayer)}</span>
                      <span style={{ display: "block", fontSize: 12, fontWeight: 400 }}>請求方法 {student.collectionPreference ? collectionMethodLabel(student.collectionPreference.collectionMethod) : "支払者未設定"}</span>
                    </button>
                  ))}
                </div>
              )}
            </section>

            {overview.capabilities.canAssignPayer ? (
              <section style={cardStyle}>
                <h2 style={{ margin: 0 }}>支払者を登録</h2>
                <p style={mutedStyle}>保護者関係から自動推定せず、対象のプラットフォーム利用者を明示的に選択します。</p>
                <form onSubmit={submitPayer} style={gridStyle}>
                  <select aria-label="支払者にする利用者" value={payerMemberId} onChange={(event) => setPayerMemberId(event.target.value)} style={inputStyle} required>
                    <option value="">利用者を選択</option>
                    {members.filter((member) => member.memberStatus === "active" && (member.roles.includes("parent") || member.roles.includes("student"))).map((member, index) => <option key={member.platformUserId} value={member.platformUserId}>{member.appNickname ?? member.lineDisplayNameSnapshot ?? `表示名未設定の利用者 ${index + 1}`}</option>)}
                  </select>
                  <button disabled={mutationState === "submitting"} style={buttonStyle}>支払者として登録</button>
                </form>
              </section>
            ) : null}

            {detail ? (
              <>
                <section style={cardStyle}>
                  <h2 style={{ margin: 0 }}>{detail.student.displayName}</h2>
                  <p style={mutedStyle}>状態: {detail.student.status === "active" ? "有効" : "無効"}</p>
                  <p style={{ margin: 0 }}>通常月謝: <strong>{yen(detail.regularSetting?.amount)}</strong></p>
                  <p style={{ margin: 0 }}>現在の支払者: <strong>{currentPayer ? payerLabel(currentPayer, members) : "未設定"}</strong></p>
                  <p style={{ margin: 0 }}>支払方法: <strong>{paymentLabel(currentPayer)}</strong></p>
                  <p style={{ margin: 0 }}>請求方法: <strong>{detail.collectionPreference ? collectionMethodLabel(detail.collectionPreference.collectionMethod) : "支払者未設定"}</strong></p>
                  {detail.collectionPreference ? <p style={mutedStyle}>決定元: {collectionSourceLabel(detail.collectionPreference.source)} / 契約: {contractModeLabel(detail.collectionPreference.contractPricingMode)}</p> : null}
                  <h3 style={{ marginBottom: 0 }}>通常所属</h3>
                  {detail.paidRegularEnrollments.length === 0 ? <p style={mutedStyle}>請求対象の通常所属はありません。</p> : (
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      {detail.paidRegularEnrollments.map((enrollment) => <li key={enrollment.enrollmentId}>{enrollment.lessonTitle}</li>)}
                    </ul>
                  )}
                  <h3 style={{ marginBottom: 0 }}>支払者割り当て履歴</h3>
                  {detail.payerAssignments.length === 0 ? <p style={mutedStyle}>割り当て履歴はありません。</p> : detail.payerAssignments.map((assignment) => {
                    const payer = detail.payers.find((item) => item.payerId === assignment.payerId);
                    return (
                      <div key={assignment.assignmentId} style={{ padding: 10, borderRadius: 8, background: "#f8fafc" }}>
                        <strong>{payer ? payerLabel(payer, members) : "登録済み支払者"}</strong>
                        <p style={mutedStyle}>{assignment.effectiveFrom} 〜 {assignment.effectiveTo ?? "継続中"} / {assignment.status === "active" ? "有効" : "終了"}</p>
                      </div>
                    );
                  })}
                </section>

                {canAssignPayer || canConfigureRegularAmount || canCreateOneOff ? (
                  <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}>
                    {canAssignPayer ? <form onSubmit={submitAssignment} style={cardStyle}>
                      <h3 style={{ margin: 0 }}>支払者割り当て</h3>
                      <select aria-label="割り当てる支払者" value={assignmentPayerId} onChange={(event) => setAssignmentPayerId(event.target.value)} style={inputStyle} required>
                        <option value="">支払者を選択</option>
                        {detail.payers.filter((payer) => payer.status === "active").map((payer) => <option key={payer.payerId} value={payer.payerId}>{payerLabel(payer, members)}</option>)}
                      </select>
                      <input aria-label="割り当て開始日" type="date" value={assignmentFrom} onChange={(event) => setAssignmentFrom(event.target.value)} style={inputStyle} required />
                      <button disabled={mutationState === "submitting"} style={buttonStyle}>割り当てる</button>
                    </form> : null}

                    {canConfigureRegularAmount ? <form onSubmit={submitRegularSetting} style={{ ...cardStyle, background: "#fffaf0", borderColor: "#d6b76b" }}>
                      <h3 style={{ margin: 0 }}>現行MVP検証用・通常月謝</h3>
                      <p style={mutedStyle}>新しい正規の料金設定は「レッスン料金設定」を使用してください。この入力は既存請求単位との互換・検証用です。</p>
                      <input aria-label="通常月謝（円）" type="number" min="0" step="1" value={regularAmount} onChange={(event) => setRegularAmount(event.target.value)} style={inputStyle} required />
                      <input aria-label="月謝適用開始日" type="date" value={regularFrom} onChange={(event) => setRegularFrom(event.target.value)} style={inputStyle} required />
                      <button disabled={mutationState === "submitting"} style={buttonStyle}>月謝を保存</button>
                    </form> : null}

                    {detail.capabilities.canConfigureCollectionMethod && activeAssignment ? <form onSubmit={submitCollectionPreference} style={cardStyle}>
                      <h3 style={{ margin: 0 }}>請求方法</h3>
                      <p style={mutedStyle}>管理利用型ではテナント徴収に固定されます。制限状態だけでは請求方法は変わりません。</p>
                      <select aria-label="請求方法" value={collectionMethod} onChange={(event) => setCollectionMethod(event.target.value as "card" | "tenantCollection")} style={inputStyle}>
                        {detail.commercialAuthority.contractPricingMode === "paymentProcessing" ? <option value="card">カード決済</option> : null}
                        <option value="tenantCollection">テナント徴収</option>
                      </select>
                      <input aria-label="請求方法適用開始日" type="date" value={collectionFrom} onChange={(event) => setCollectionFrom(event.target.value)} style={inputStyle} required />
                      <input aria-label="請求方法適用終了日" type="date" value={collectionTo} onChange={(event) => setCollectionTo(event.target.value)} style={inputStyle} />
                      <button disabled={mutationState === "submitting"} style={buttonStyle}>請求方法を保存</button>
                    </form> : null}

                    {canConfigureRegularAmount ? <form onSubmit={submitRegularUnit} style={{ ...cardStyle, background: "#fffaf0", borderColor: "#d6b76b" }}>
                      <h3 style={{ margin: 0 }}>現行MVP検証用・通常月謝の請求単位</h3>
                      <select aria-label="通常所属" value={regularEnrollmentId} onChange={(event) => setRegularEnrollmentId(event.target.value)} style={inputStyle} required>
                        <option value="">通常所属を選択</option>
                        {detail.paidRegularEnrollments.map((enrollment) => <option key={enrollment.enrollmentId} value={enrollment.enrollmentId}>{enrollment.lessonTitle}</option>)}
                      </select>
                      <input aria-label="対象月" type="month" value={regularServiceMonth} onChange={(event) => setRegularServiceMonth(event.target.value)} style={inputStyle} required />
                      <button disabled={mutationState === "submitting" || !activeAssignment || !detail.regularSetting} style={buttonStyle}>請求単位を作成</button>
                    </form> : null}

                    {canCreateOneOff ? <form onSubmit={submitOneOff} style={{ ...cardStyle, background: "#fffaf0", borderColor: "#d6b76b" }}>
                      <h3 style={{ margin: 0 }}>現行MVP検証用・単発請求</h3>
                      <p style={mutedStyle}>正規の単発請求・調整は専用メニューのプレビュー付き登録を使用してください。</p>
                      <input aria-label="単発請求額（円）" type="number" min="0" step="1" value={oneOffAmount} onChange={(event) => setOneOffAmount(event.target.value)} style={inputStyle} required />
                      <input aria-label="請求理由区分" value={oneOffSourceType} onChange={(event) => setOneOffSourceType(event.target.value)} style={inputStyle} required />
                      <input aria-label="管理用識別子（任意）" value={oneOffSourceId} onChange={(event) => setOneOffSourceId(event.target.value)} style={inputStyle} />
                      <button disabled={mutationState === "submitting" || !activeAssignment} style={buttonStyle}>単発請求を作成</button>
                    </form> : null}
                  </div>
                ) : null}

                <section style={{ ...cardStyle, background: "#fffaf0", borderColor: "#d6b76b" }}>
                  <h2 style={{ margin: 0 }}>現行MVP検証用・請求単位</h2>
                  {detail.billingUnits.length === 0 ? <p style={mutedStyle}>請求単位はありません。</p> : detail.billingUnits.map((unit) => (
                    <button key={unit.billingUnitId} type="button" onClick={() => void selectUnit(unit.billingUnitId)} style={{ ...secondaryButtonStyle, textAlign: "left" }}>
                      {unit.chargeType === "regularMonthly" ? `${unit.serviceMonth} 通常月謝` : "単発請求"} / {yen(unit.currentPayableAmount)} / {unit.status === "draft" ? "下書き" : unit.status === "locked" ? "確定" : unit.status}
                    </button>
                  ))}
                </section>
              </>
            ) : null}

            {unitDetail ? (
              <section style={{ ...cardStyle, background: "#fffaf0", borderColor: "#d6b76b" }}>
                <h2 style={{ margin: 0 }}>現行MVP検証用・請求単位の詳細</h2>
                <p style={{ margin: 0 }}>初期金額: {yen(unitDetail.billingUnit.defaultAmount)}</p>
                <p style={{ margin: 0 }}>現在金額: {yen(unitDetail.billingUnit.currentPayableAmount)}</p>
                <p style={{ margin: 0 }}>状態: {unitDetail.billingUnit.status === "draft" ? "下書き" : unitDetail.billingUnit.status === "locked" ? "確定" : unitDetail.billingUnit.status}</p>
                {unitDetail.capabilities.canAdjustAmount && unitDetail.billingUnit.status === "draft" ? (
                  <form onSubmit={(event) => {
                    event.preventDefault();
                    void mutate(() => adjustStudentBillingAdminUnit(tenantSlug, unitDetail.billingUnit.billingUnitId, {
                      expectedVersion: unitDetail.billingUnit.version,
                      newAmount: Number(adjustmentAmount),
                      currency: "JPY",
                      reason: adjustmentReason
                    }), "金額を調整しました。");
                  }} style={gridStyle}>
                    <input aria-label="調整後金額（円）" type="number" min="0" step="1" value={adjustmentAmount} onChange={(event) => setAdjustmentAmount(event.target.value)} style={inputStyle} required />
                    <textarea aria-label="調整理由" value={adjustmentReason} onChange={(event) => setAdjustmentReason(event.target.value)} style={inputStyle} maxLength={500} required />
                    <button disabled={mutationState === "submitting"} style={buttonStyle}>金額を調整</button>
                  </form>
                ) : null}
                {unitDetail.capabilities.canLockBillingUnit && unitDetail.billingUnit.status === "draft" ? (
                  <button type="button" onClick={() => void mutate(() => lockStudentBillingAdminUnit(tenantSlug, unitDetail.billingUnit.billingUnitId, { expectedVersion: unitDetail.billingUnit.version }), "請求単位を確定しました。")} style={buttonStyle}>請求単位を確定</button>
                ) : null}
                {paymentExecution ? (
                  <div style={{ padding: 12, borderRadius: 8, background: "#f8fafc" }}>
                    <strong>決済状況: {paymentExecution.status}</strong>
                    <p style={mutedStyle}>決済金額: {yen(paymentExecution.amount)}</p>
                    <p style={mutedStyle}>決済予定日: {paymentExecution.scheduledChargeDate}</p>
                    <p style={mutedStyle}>実行日時: {paymentExecution.attemptedAt ? dateTimeJa(paymentExecution.attemptedAt) : "未実行"}</p>
                    <p style={mutedStyle}>支払完了日時: {paymentExecution.paidAt ? dateTimeJa(paymentExecution.paidAt) : "未完了"}</p>
                    {paymentExecution.safeFailureCode ? <p style={mutedStyle}>再確認が必要です。コード: {paymentExecution.safeFailureCode}</p> : null}
                    {unitDetail.capabilities.canExecutePayment && paymentExecution.canRetry ? (
                      <button type="button" onClick={() => void mutate(async () => {
                        await retryStudentBillingAdminPayment(tenantSlug, unitDetail.billingUnit.billingUnitId, paymentExecution.executionId, unitDetail.billingUnit.version);
                      }, "決済を再試行しました。") } style={buttonStyle}>決済を再試行</button>
                    ) : null}
                  </div>
                ) : null}
                {unitDetail.capabilities.canExecutePayment
                  && unitDetail.billingUnit.status === "locked"
                  && !paymentExecution
                  && unitPayer?.paymentMethod.paymentMethodReady
                  && unitPayer.paymentMethod.card ? (
                  <div style={gridStyle}>
                    <strong>決済内容の確認</strong>
                    <p style={mutedStyle}>生徒: {detail?.student.displayName ?? "確認できません"}</p>
                    <p style={mutedStyle}>支払者: {unitPayer ? payerLabel(unitPayer, members) : unitDetail.billingUnit.payerId}</p>
                    <p style={mutedStyle}>支払方法: {paymentLabel(unitPayer)}</p>
                    <p style={mutedStyle}>請求区分: {unitDetail.billingUnit.chargeType === "regularMonthly" ? "通常月謝" : "単発請求"}</p>
                    {unitDetail.billingUnit.serviceMonth ? <p style={mutedStyle}>対象月: {unitDetail.billingUnit.serviceMonth}</p> : null}
                    <p style={mutedStyle}>確定金額 {yen(unitDetail.billingUnit.currentPayableAmount)} を登録済み支払方法で決済します。実行時に金額は変更できません。</p>
                    <button type="button" onClick={() => void mutate(async () => {
                      await executeStudentBillingAdminPayment(tenantSlug, unitDetail.billingUnit.billingUnitId, unitDetail.billingUnit.version);
                    }, "決済を実行しました。") } style={buttonStyle}>決済を実行</button>
                  </div>
                ) : null}
                <h3 style={{ marginBottom: 0 }}>金額調整履歴</h3>
                {unitDetail.adjustments.length === 0 ? <p style={mutedStyle}>調整履歴はありません。</p> : unitDetail.adjustments.map((adjustment) => (
                  <div key={adjustment.adjustmentId} style={{ padding: 12, borderRadius: 8, background: "#f8fafc" }}>
                    <strong>{yen(adjustment.previousAmount)} → {yen(adjustment.newAmount)}</strong>
                    <p style={mutedStyle}>変更者: {studentBillingAdjustmentActorLabel(adjustment.actorPlatformUserId, members)}</p>
                    <p style={mutedStyle}>変更日時: {dateTimeJa(adjustment.changedAt)}</p>
                    <p style={mutedStyle}>理由: {adjustment.reason}</p>
                  </div>
                ))}
              </section>
            ) : null}
          </>
        ) : null}
      </div>
    </OperatorPageScaffold>
  );
}
